import { staticAsset } from '../asset-urls.js';

const FALLBACK_ORIGIN = 'https://www.trenchmate.co.nz';
const DEFAULT_SITE_NAME = 'Trenchmate';
const DEFAULT_DESCRIPTION =
    'Trenchmate delivers trench shields, shoring boxes, and excavation safety equipment across New Zealand. Hire trench shoring systems for safe, efficient construction.';
const DEFAULT_KEYWORDS = Object.freeze([
    'trench shoring',
    'trench shields',
    'shoring solutions',
    'trench boxes',
    'excavation safety',
    'trenchmate',
    'trench shoring nz',
    'shoring equipment',
    'trench protection',
    'construction safety',
]);
const DEFAULT_SOCIAL_IMAGE = Object.freeze({
    url: '/images/og/home.webp',
    width: 1200,
    height: 630,
    alt: 'Trenchmate trench shoring equipment on site',
});

let cachedOrigin = null;
let warnedInvalidOrigin = false;

export function getSiteOrigin() {
    if (cachedOrigin) {
        return cachedOrigin;
    }

    const candidates = [process.env.NEXT_PUBLIC_SITE_URL, process.env.SITE_URL];

    for (const candidate of candidates) {
        if (!candidate) continue;
        try {
            const origin = new URL(candidate).origin;
            if (origin) {
                cachedOrigin = origin;
                return cachedOrigin;
            }
        } catch (_err) {
            if (!warnedInvalidOrigin) {
                console.warn(
                    '[metadata] Ignoring invalid site URL:',
                    candidate,
                );
                warnedInvalidOrigin = true;
            }
        }
    }

    cachedOrigin = FALLBACK_ORIGIN;
    return cachedOrigin;
}

export function toAbsoluteUrl(pathOrUrl) {
    if (!pathOrUrl) return undefined;

    try {
        return new URL(pathOrUrl, getSiteOrigin()).toString();
    } catch (err) {
        if (!warnedInvalidOrigin) {
            console.warn(
                '[metadata] Unable to resolve URL for:',
                pathOrUrl,
                err,
            );
            warnedInvalidOrigin = true;
        }
        return undefined;
    }
}

export function toAbsoluteAssetUrl(pathOrUrl) {
    if (!pathOrUrl) return undefined;

    return toAbsoluteUrl(staticAsset(pathOrUrl));
}

function normalizeImages(images, fallbackAlt) {
    if (!images) return [];

    const values = Array.isArray(images) ? images : [images];
    return values
        .map((image) => {
            if (!image) return null;

            if (typeof image === 'string') {
                const url = toAbsoluteAssetUrl(image);
                if (!url) return null;
                return {
                    url,
                    alt: fallbackAlt || DEFAULT_SOCIAL_IMAGE.alt,
                };
            }

            if (typeof image === 'object' && image.url) {
                const url = toAbsoluteAssetUrl(image.url);
                if (!url) return null;
                return {
                    ...image,
                    url,
                    alt: image.alt || fallbackAlt || DEFAULT_SOCIAL_IMAGE.alt,
                };
            }

            return null;
        })
        .filter(Boolean);
}

function resolveKeywords(keywords) {
    if (!keywords) {
        return [...DEFAULT_KEYWORDS];
    }

    const source = Array.isArray(keywords) ? keywords : [keywords];
    return Array.from(new Set(source.filter(Boolean)));
}

function resolveRobots(robots) {
    if (robots) return robots;

    return {
        index: true,
        follow: true,
        googleBot: {
            index: true,
            follow: true,
            'max-video-preview': -1,
            'max-image-preview': 'large',
            'max-snippet': -1,
        },
    };
}

export function buildMetadata(options = {}) {
    const {
        title,
        description = DEFAULT_DESCRIPTION,
        path = '/',
        keywords,
        openGraph = {},
        twitter = {},
        robots,
        alternates,
        images,
        ...rest
    } = options;

    const canonical = toAbsoluteUrl(path) || getSiteOrigin();
    const socialTitle =
        typeof title === 'string' ? title : title?.default || DEFAULT_SITE_NAME;
    const socialDescription = description || DEFAULT_DESCRIPTION;

    const openGraphImages = normalizeImages(
        openGraph.images || images || DEFAULT_SOCIAL_IMAGE,
        socialTitle,
    );
    const twitterImages = normalizeImages(
        twitter.images || openGraphImages,
        socialTitle,
    );

    const resolvedAlternates = { ...(alternates || {}) };
    if (!resolvedAlternates.canonical) {
        resolvedAlternates.canonical = canonical;
    }

    return {
        title,
        description,
        keywords: resolveKeywords(keywords),
        alternates: resolvedAlternates,
        openGraph: {
            type: openGraph.type || 'website',
            url: canonical,
            siteName: openGraph.siteName || DEFAULT_SITE_NAME,
            title: openGraph.title || socialTitle,
            description: openGraph.description || socialDescription,
            ...openGraph,
            images: openGraphImages,
        },
        twitter: {
            card: twitter.card || 'summary_large_image',
            title: twitter.title || socialTitle,
            description: twitter.description || socialDescription,
            ...twitter,
            images: twitterImages,
        },
        robots: resolveRobots(robots),
        ...rest,
    };
}

export const siteMetadataDefaults = Object.freeze({
    siteName: DEFAULT_SITE_NAME,
    description: DEFAULT_DESCRIPTION,
    keywords: [...DEFAULT_KEYWORDS],
    socialImage: { ...DEFAULT_SOCIAL_IMAGE },
});

export function resetSiteOriginCache() {
    cachedOrigin = null;
    warnedInvalidOrigin = false;
}
